/*
  Product page urgency.

  Two live pieces, both driven by real data:

  1. Dispatch cutoff. The seconds remaining are calculated server side in the
     SHOP's timezone and handed to this script, which only counts down. Doing
     the maths in the browser would use the visitor's clock and tell someone
     in Tokyo the wrong cutoff.

  2. Free shipping progress, read from the live cart.
*/
class PdpUrgency extends HTMLElement {
  connectedCallback() {
    this.clock = this.querySelector('[data-urgency-clock]');
    this.cutoffWrap = this.querySelector('[data-urgency-cutoff]');
    this.shipWrap = this.querySelector('[data-urgency-shipping]');

    if (this.clock) this.startCountdown();
    if (this.shipWrap) this.updateShipping();

    // Adding to the cart changes the free shipping gap.
    this.onCartChange = () => this.updateShipping();
    document.addEventListener('cart:refresh', this.onCartChange);
    document.addEventListener('cart:updated', this.onCartChange);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('cart:refresh', this.onCartChange);
    document.removeEventListener('cart:updated', this.onCartChange);
  }

  startCountdown() {
    let remaining = parseInt(this.dataset.secondsLeft || '0', 10);
    if (!remaining || remaining <= 0) return;

    const tick = () => {
      if (remaining <= 0) {
        clearInterval(this.timer);
        // The window closed while they were on the page. Hide rather than
        // show a stale promise.
        if (this.cutoffWrap) this.cutoffWrap.hidden = true;
        return;
      }

      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;

      this.clock.textContent =
        (h > 0 ? h + 'h ' : '') + m + 'm ' + String(s).padStart(2, '0') + 's';

      remaining -= 1;
    };

    tick();
    this.timer = setInterval(tick, 1000);
  }

  async updateShipping() {
    const threshold = parseInt(this.dataset.freeShippingThreshold || '0', 10);
    if (!threshold) return;

    let cart;
    try {
      cart = await (await fetch('/cart.js')).json();
    } catch (e) {
      return;
    }

    const total = cart.total_price || 0;
    const remaining = threshold - total;
    const bar = this.shipWrap.querySelector('[data-urgency-ship-bar]');
    const text = this.shipWrap.querySelector('[data-urgency-ship-text]');

    const pct = Math.min(100, Math.round((total / threshold) * 100));
    if (bar) bar.style.setProperty('--urg-ship-fill', pct + '%');

    if (!text) return;

    if (remaining <= 0) {
      text.textContent = this.dataset.shippingUnlocked || 'Free shipping unlocked';
      this.shipWrap.classList.add('is-unlocked');
    } else {
      const money = this.formatMoney(remaining);
      text.innerHTML = (this.dataset.shippingPrefix || 'You are')
        + ' <strong>' + money + '</strong> '
        + (this.dataset.shippingSuffix || 'from free shipping');
      this.shipWrap.classList.remove('is-unlocked');
    }
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const whole = cents % 100 === 0;
    const amount = whole ? String(cents / 100) : (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)));
  }
}

if (!customElements.get('pdp-urgency')) {
  customElements.define('pdp-urgency', PdpUrgency);
}
